/**
 * SearchBar molecule - combines search input with optional filters
 * Provides consistent search functionality across the application
 */

import {
  Box,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import { FiChevronDown, FiFilter } from 'react-icons/fi';
import { Button } from '../../atoms/buttons/Button';
import { SearchInput, SearchInputProps } from '../../atoms/inputs/SearchInput';
import { BaseComponentProps } from '../../types';

export interface FilterOption {
  /** Filter key/value */
  value: string;
  /** Display label */
  label: string;
}

export interface SearchBarProps extends BaseComponentProps, SearchInputProps {
  /** Filter options */
  filters?: FilterOption[];
  /** Currently selected filter */
  selectedFilter?: string;
  /** Callback when filter is changed */
  onFilterChange?: (filter: string) => void;
  /** Additional action buttons */
  actions?: React.ReactNode;
  /** Whether to show filter dropdown */
  showFilters?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  filters = [],
  selectedFilter,
  onFilterChange,
  actions,
  showFilters = true,
  className,
  testId,
  ...searchProps
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  const selectedFilterLabel =
    filters.find((f) => f.value === selectedFilter)?.label || 'All';
  const hasFilters = showFilters && filters.length > 0;

  return (
    <HStack
      spacing={4}
      p={4}
      bg={bgColor}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      className={className}
      data-testid={testId}
    >
      <Box flex="1">
        <SearchInput {...searchProps} />
      </Box>

      {hasFilters && (
        <Menu>
          <MenuButton
            as={Button}
            variant="secondary"
            rightIcon={<FiChevronDown />}
            leftIcon={<FiFilter />}
            size="md"
            minWidth="120px"
          >
            {selectedFilterLabel}
          </MenuButton>
          <MenuList>
            {filters.map((filter) => (
              <MenuItem
                key={filter.value}
                onClick={() => onFilterChange?.(filter.value)}
                fontWeight={
                  selectedFilter === filter.value ? 'semibold' : 'normal'
                }
              >
                {filter.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}

      {actions && <HStack spacing={2}>{actions}</HStack>}
    </HStack>
  );
};
